<template>
  <div class="lh">
    <LearningHubHeader />

    <div v-if="loading" class="lh-loading">Loading…</div>

    <div v-else-if="!course" class="lh-notfound">
      <div class="wrap">
        <div class="panel">
          <h2>We couldn't find that course</h2>
          <p class="body-copy">
            The link may be wrong, or you may not be enrolled in this course
            yet. If you believe you should have access, email
            <a href="mailto:info@gdq.se">info@gdq.se</a>.
          </p>
        </div>
      </div>
    </div>

    <div v-else-if="!progressLoaded" class="lh-loading">Loading…</div>

    <div v-else-if="!enrolment" class="lh-notfound">
      <div class="wrap">
        <div class="panel">
          <h2>You're not enrolled in this course yet</h2>
          <p class="body-copy">
            Enrolment is handled by the GDQ team. To get started, email
            <a href="mailto:info@gdq.se">info@gdq.se</a>.
          </p>
        </div>
      </div>
    </div>

    <template v-else>
      <LearningHubHero
        :course="course"
        @process="setTab('process')"
        @practical="setTab('practical')"
      />
      <EnrolmentStrip :course="course" />

      <section class="welcome">
        <div class="wrap">
          <div class="panel">
            <div class="body-copy" v-html="course.welcome_1"></div>
            <div class="body-copy no-mb" v-html="course.welcome_2"></div>
          </div>
        </div>
      </section>

      <section id="process" class="process-section">
        <div class="wrap">
          <ProcessTabs
            :course="course"
            :steps-done="stepsDone"
            :substeps-done="substepsDone"
            :active="activeTab"
            @tab="setTab"
            @step-toggle="stepToggle"
            @substep-toggle="substepToggle"
          />
        </div>
      </section>

      <section id="material" class="lh-section">
        <div class="wrap">
          <MaterialsSection :course="course" />
        </div>
      </section>

      <section id="trainers" class="lh-section">
        <div class="wrap">
          <TrainersSection
            :course="course"
            @open-sue="sueOpen = true"
          />
        </div>
      </section>

      <section id="payment" class="lh-section">
        <div class="wrap">
          <PaymentTerms :course="course" />
        </div>
      </section>

      <section id="account" class="lh-section">
        <div class="wrap">
          <AccountForm />
        </div>
      </section>

      <footer class="lh-footer">
        <div class="wrap foot-bottom">
          <span>© 2026 GDQ Associates · Learning Hub</span>
          <span>Privacy policy · Cookies</span>
        </div>
      </footer>

      <SueChat
        :open="sueOpen"
        :kb="course.sue_kb"
        :trainers="course.trainers"
        @open="sueOpen = true"
        @close="sueOpen = false"
        @action="sueAction"
      />
    </template>
  </div>
</template>

<script>
import LearningHubHeader from './LearningHubHeader.vue'
import LearningHubHero from './LearningHubHero.vue'
import EnrolmentStrip from './EnrolmentStrip.vue'
import ProcessTabs from './ProcessTabs.vue'
import MaterialsSection from './MaterialsSection.vue'
import TrainersSection from './TrainersSection.vue'
import PaymentTerms from './PaymentTerms.vue'
import AccountForm from './AccountForm.vue'
import SueChat from './SueChat.vue'

export default {
  name: 'LearningHubCourse',
  components: {
    LearningHubHeader,
    LearningHubHero,
    EnrolmentStrip,
    ProcessTabs,
    MaterialsSection,
    TrainersSection,
    PaymentTerms,
    AccountForm,
    SueChat
  },
  data () {
    return {
      activeTab: 'process',
      sueOpen: false
    }
  },
  computed: {
    course () {
      return this.$store.state.learninghub.course
    },
    stepsDone () {
      return this.$store.state.learninghub.steps_done
    },
    substepsDone () {
      return this.$store.state.learninghub.substeps_done
    },
    loading () {
      return this.$store.state.learninghub.loading
    },
    enrolment () {
      return this.$store.state.learninghub.enrolment
    },
    progressLoaded () {
      return this.$store.state.learninghub.progress_loaded
    }
  },
  mounted () {
    this.$store
      .dispatch('learninghub_course_fetch', { slug: this.$route.params.slug })
      .then(() => {
        return this.$store.dispatch('learninghub_progress_fetch', {
          course_id: this.course.id
        })
      })
      .catch(() => {})
  },
  methods: {
    setTab (tab) {
      this.activeTab = tab
    },
    stepToggle (stepId) {
      this.$store.dispatch('learninghub_step_toggle', {
        step_id: stepId,
        done: !this.stepsDone.includes(stepId)
      })
    },
    substepToggle (substepId) {
      this.$store.dispatch('learninghub_substep_toggle', {
        substep_id: substepId,
        done: !this.substepsDone.includes(substepId)
      })
    },
    sueAction (target) {
      if (target === 'human') return
      if (target === 'practical') {
        this.activeTab = 'practical'
        target = 'process'
      }
      const el = document.getElementById(target)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}
</script>

<style scoped>
.lh {
  --ink: #00303a;
  --ink-soft: #294a50;
  --paper: #f4f6f7;
  --paper-2: #e8edee;
  --teal: #007a8f;
  --teal-dk: #00545f;
  --berry: #86013c;
  --coral: #9c2150;
  --line: rgba(13, 27, 42, 0.12);
  --white: #fff;
  --ok: #1f9d6b;
  --maxw: 1180px;
  min-height: 100vh;
  background: var(--paper);
  color: var(--ink);
  line-height: 1.6;
}
.wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 24px;
}
.lh-loading {
  max-width: var(--maxw);
  margin: 60px auto;
  padding: 0 24px;
  color: var(--ink-soft);
}
.lh-notfound {
  padding: 60px 0;
}
.panel {
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 24px;
}
.body-copy {
  color: var(--ink-soft);
  font-size: 0.96rem;
  margin-bottom: 10px;
}
.no-mb {
  margin-bottom: 0;
}
.welcome {
  padding: 26px 0 40px;
}
.process-section {
  padding-top: 0;
}
.lh-section {
  padding-top: 0;
  padding-bottom: 40px;
}
.lh-footer {
  border-top: 1px solid var(--line);
  background: var(--white);
  padding: 18px 0;
}
.foot-bottom {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: var(--ink-soft);
}
</style>
